import { CartItem, CashierOrderHistory, invoice } from "@/types/ApiTypes";
import { AppConfig } from "@/utils/AppConfig";
import { handlePrintForAllPlatforms } from "@/utils/PrintForAllPlatforms";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { IoMdClose } from "react-icons/io";
import { useReactToPrint } from "react-to-print";
import { useTranslation } from "react-i18next";
import { formatMoney } from "@/utils/helpers";
import { useSelector } from "react-redux";
import axios from "axios";

interface CashierGroupInvoiceProps {
  toggleCashierModal: () => void;
  groupId: string;
  firstAcceptedOrder: CashierOrderHistory;
  isGroupPaid: boolean;
}

export default function CashierGroupInvoice({
  toggleCashierModal,
  groupId,
  firstAcceptedOrder,
  isGroupPaid
}: CashierGroupInvoiceProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [invoiceData, setInvoiceData] = useState<invoice | null>(null);
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const BASE_URL = import.meta.env.VITE_BASE_URL;

  const groupedOrderUrl = `${BASE_URL}/api/dashboard/cashier/grouped-orders/${groupId}/invoice?locale=${locale}`;

  useEffect(() => {
    const loader = toast.loading(t("cashierHistoryBody.loading") as string);
    const fetchInvoice = async () => {
      try {
        const res = await axios.get(groupedOrderUrl, {
          headers: { Authorization: token },
        });

        if (res.data.success === false) {
          toast.error(res.data.error);
          return;
        }

        setInvoiceData(res.data.invoice);
      } catch (error: unknown) {
        const message = axios.isAxiosError(error)
          ? error.response?.data?.error || t("products.categoriesFetchError")
          : t("products.unexpectedError");

        toast.error(message as string);
      } finally {
        toast.dismiss(loader);
      }
    };

    fetchInvoice();
  }, [groupedOrderUrl, t, token]);

  const contentRef = useRef<HTMLDivElement>(null);
  const toastIdRef = useRef<string | undefined>(undefined);
  const [printLoading, setPrintLoading] = useState(false);
  const reactToPrintFn = useReactToPrint({
    contentRef,
    onBeforePrint: async () => {
      setPrintLoading(true);
      return Promise.resolve(); // or simply: return;
    },
    onAfterPrint: () => {
      setPrintLoading(false);
    },
    onPrintError: () => {
      setPrintLoading(false);
    },
  });
  useEffect(() => {
    if (printLoading) {
      // Show loading toast and store the ID
      toastIdRef.current = toast.loading(
        t("cashierHistoryModal.loading") as string,
      );
    } else {
      if (toastIdRef.current) {
        // Dismiss loading toast
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = undefined;

        // Show success toast
        // toast.success(t("cashierHistoryModal.printed") as string);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printLoading]);

  if (!invoiceData) return null;

  return (
    <>
      {invoiceData && (
        <>
          <div
            dir="ltr"
            ref={contentRef}
            className="invoice-container print hidden text-lg font-semibold"
          >
            <div className="flex flex-col items-center text-center">
              <div className="h-auto w-44">
                <img
                  src={invoiceData.logo}
                  alt="Logo"
                  className="h-auto w-full"
                />
              </div>

              <h1 className="flex flex-col text-xl font-bold">
                <span>
                  {invoiceData.restaurantData.nameAR ===
                  invoiceData.restaurantData.nameEN
                    ? ""
                    : invoiceData.restaurantData.nameAR}
                </span>
                <span>{invoiceData.restaurantData.nameEN}</span>
              </h1>
              <h1 className="flex flex-col text-xl font-bold">
                <span>
                  {invoiceData.branch.nameAR === invoiceData.branch.nameEN
                    ? ""
                    : invoiceData.branch.nameAR}
                </span>
                <span>{invoiceData.branch.nameEN}</span>
              </h1>
              <span className="mt-1 font-semibold">
                {invoiceData.branch.address}
              </span>
              <span className="mt-1 font-semibold">
                {invoiceData.branch.phone}
              </span>

              {invoiceData.taxNumber && (
                <>
                  <div className="my-2 flex w-full flex-col justify-between font-semibold">
                    <span>الرقم الضريبي </span>
                    <span>Tax number</span>
                  </div>
                  <div className="flex flex-col items-center gap-2 text-lg">
                    <span className="font-bold">{invoiceData.taxNumber}</span>
                  </div>
                </>
              )}

              <div className="my-2 flex w-full flex-col justify-between font-semibold">
                <span>فاتورة ضريبية مبسطة</span>
                <span>Simple tax invoice</span>
              </div>
              <span
                dir="ltr"
                className="my-3 flex w-fit gap-2 border-2 border-black p-2 text-2xl font-bold"
              >
                Order# <div>{invoiceData?.orderId}</div>
              </span>

              <span dir="ltr" className="flex gap-1 text-lg font-semibold">
                Unique order ID# <div>{invoiceData?._id}</div>
              </span>
              <span className="text-md flex items-center gap-1 font-semibold">
                Printed at : <div>{new Date().toLocaleString("en-US")}</div>
              </span>
              <div className="text-md flex items-center gap-1 font-semibold">
                <span> Order time : </span>
                <span>{invoiceData.deliverTime}</span>
                <span> : وقت الطلب </span>
              </div>
            </div>

            <div className="w-full border-b-2 border-b-black" />

            <div className="text-center font-semibold">
              Cashier
              {firstAcceptedOrder?.dineIn?.floor &&
                firstAcceptedOrder?.dineIn?.table && (
                  <div className="flex justify-between text-sm">
                    <div>
                      Floor {firstAcceptedOrder?.dineIn?.floor.number || ""}{" "}
                      - Table{" "}
                      {firstAcceptedOrder?.dineIn?.table.number || ""}
                    </div>
                    <div dir="rtl">
                      طابق {firstAcceptedOrder?.dineIn?.floor.number || ""} -
                      طاولة {firstAcceptedOrder?.dineIn?.table.number || ""}
                    </div>
                  </div>
                )}
            </div>

            <div className="w-full border-b-2 border-b-black" />

            <div className="space-y-1">
              <div className="flex justify-between font-bold" dir="rtl">
                <div className="text-start">Price</div>
                <div className="flex gap-5">
                  <span>item</span>
                  <span>Qty</span>
                </div>
              </div>
              <div className="my-2 w-full border-b-2 border-b-black" />
              <div className="font-semibold">
                {invoiceData.items.map((item: CartItem, index) => {
                  return (
                    <div key={index} className="flex flex-col gap-1">
                      <div className="flex items-center justify-between gap-10">
                        <span
                          className="flex flex-row-reverse gap-5 whitespace-normal"
                          dir="rtl"
                        >
                          <span>{item.quantity} </span>
                          <span>
                            <span className="flex flex-col gap-1">
                              {item.nameEN}
                            </span>
                            <span>
                              {item.nameAR === item.nameEN ? "" : item.nameAR}
                            </span>
                          </span>
                        </span>
                        <span
                          dir="ltr"
                          className="flex items-center gap-1 self-start"
                        >
                          {" "}
                          {AppConfig.currency.icon()}
                          {formatMoney(item.totalPriceWithTaxes)}
                        </span>
                      </div>
                      {item.addons.length > 0 && (
                        <span className="flex flex-col font-bold">
                          <span className="text-center">
                            {t("orders.addons")}
                          </span>
                          <span className="flex w-full flex-col gap-1 font-normal">
                            {item.addons.map((addon, i) => {
                              const hasPrice = addon.price && addon.price > 0;
                              return hasPrice ? (
                                <div
                                  key={i}
                                  className="flex w-full justify-between text-sm"
                                >
                                  <div></div>
                                  <span className="text-right">
                                    {addon.nameEN}|{addon.nameAR}
                                  </span>
                                  <span dir="ltr">
                                    {AppConfig.currency.icon()}{" "}
                                    {formatMoney(addon.price)}
                                  </span>
                                </div>
                              ) : (
                                <div
                                  key={i}
                                  className="flex w-full justify-center text-sm"
                                >
                                  <span className="text-right">
                                    {addon.nameEN} | {addon.nameAR}
                                  </span>
                                </div>
                              );
                            })}
                          </span>
                        </span>
                      )}

                      {item.options.length > 0 && (
                        <span className="flex flex-col font-bold">
                          <span className="text-center">
                            {t("ordersBody.Options")}
                          </span>
                          <span className="flex w-full flex-col gap-1 font-normal">
                            {item.options.map((option, i) =>
                              option.options.map((opt, j) => {
                                const hasPrice = opt.price && opt.price > 0;
                                return hasPrice ? (
                                  <div
                                    key={`${i}-${j}`}
                                    className="flex w-full justify-between text-sm"
                                  >
                                    <div></div> {/* Optional for alignment */}
                                    <span className="text-right">
                                      {opt.nameEN}|{opt.nameAR}
                                    </span>
                                    {opt.price && opt.price > 0 && (
                                      <span dir="ltr">
                                        {AppConfig.currency.icon()}{" "}
                                        {formatMoney(opt.price)}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <div
                                    key={`${i}-${j}`}
                                    className="flex w-full justify-center text-sm"
                                  >
                                    <span className="text-right">
                                      {opt.nameEN} | {opt.nameAR}
                                    </span>
                                  </div>
                                );
                              }),
                            )}
                          </span>
                        </span>
                      )}
                      {invoiceData.items.length - 1 !== index && (
                        <div className="w-full border-b-2 border-b-black" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {invoiceData.subtotal > 0 && (
              <>
                <div className="col-span-full w-full border-b-2 border-b-black" />
                <div
                  dir="ltr"
                  className="ml-auto flex w-[80%] items-center justify-between font-semibold"
                >
                  <div className="flex w-[50%] flex-col text-end">
                    <div>Sub total</div>
                    <div>المجموع الفرعي</div>
                  </div>
                  <div className="text-center">
                    {AppConfig.currency.icon()}{" "}
                    {formatMoney(invoiceData.subtotal)}
                  </div>
                </div>
              </>
            )}

            {invoiceData.discount > 0 && (
              <div
                dir="ltr"
                className="ml-auto mt-2 flex w-[80%] items-center justify-between font-semibold"
              >
                <div className="flex w-[50%] flex-col text-end">
                  <div>Discount</div>
                  <div>الخصم</div>
                </div>
                <div className="text-center">
                  {AppConfig.currency.icon()}{" "}
                  {formatMoney(invoiceData.discount)}
                </div>
              </div>
            )}

            {invoiceData.taxes > 0 && (
              <div
                dir="ltr"
                className="ml-auto mt-2 flex w-[80%] items-center justify-between font-semibold"
              >
                <div className="flex w-[50%] flex-col text-end">
                  <div>Taxes (15%)</div>
                  <div>الضرائب (15%)</div>
                </div>
                <div className="text-center">
                  {AppConfig.currency.icon()} {formatMoney(invoiceData.taxes)}
                </div>
              </div>
            )}


              <div
                dir="ltr"
                className="ml-auto mt-2 flex w-[80%] items-center justify-between font-semibold"
              >
                <div className="flex w-[50%] flex-col text-end">
                  <div>Service Fees</div>
                  <div>رسوم الخدمة</div>
                </div>
                <div className="text-center">
                  {AppConfig.currency.icon()} {formatMoney(invoiceData.serviceFees || 0)}
                </div>
              </div>

            {invoiceData.total > 0 && (
              <>
                <div className="col-span-full mt-2 w-full border-b-2 border-b-black" />
                <div
                  dir="ltr"
                  className="ml-auto mt-2 flex w-[80%] items-center justify-between font-semibold"
                >
                  <div className="flex w-[50%] flex-col text-end">
                    <div>Total</div>
                    <div>المجموع</div>
                  </div>
                  <div className="text-center">
                    {AppConfig.currency.icon()} {formatMoney(invoiceData.total)}
                  </div>
                </div>
              </>
            )}

            {firstAcceptedOrder.status !== "rejected" ? (
              <>
                {firstAcceptedOrder.cashPaid > 0 && (
                  <>
                    <div className="col-span-full mt-2 w-full border-b-2 border-b-black" />
                    <div
                      dir="ltr"
                      className="ml-auto mt-2 flex w-[80%] items-center justify-between font-semibold"
                    >
                      <div className="flex w-[50%] flex-col text-end">
                        <div>Total Cash Paid</div>
                        <div>إجمالي النقد المدفوع</div>
                      </div>
                      <div className="text-center">
                        {AppConfig.currency.icon()}{" "}
                        {formatMoney(firstAcceptedOrder.cashPaid)}
                      </div>
                    </div>
                  </>
                )}

                {firstAcceptedOrder.networkPaid > 0 && (
                  <>
                    <div className="col-span-full mt-2 w-full border-b-2 border-b-black" />
                    <div
                      dir="ltr"
                      className="ml-auto mt-2 flex w-[80%] items-center justify-between font-semibold"
                    >
                      <div className="flex w-[50%] flex-col text-end">
                        <div>Total Credit Paid</div>
                        <div>إجمالي الشبكة المدفوعة</div>
                      </div>
                      <div className="text-center">
                        {AppConfig.currency.icon()}{" "}
                        {formatMoney(firstAcceptedOrder.networkPaid)}
                      </div>
                    </div>
                  </>
                )}

                {firstAcceptedOrder.remainingCash > 0 && (
                  <>
                    <div className="col-span-full mt-2 w-full border-b-2 border-b-black" />
                    <div
                      dir="ltr"
                      className="ml-auto mt-2 flex w-[80%] items-center justify-between font-semibold"
                    >
                      <div className="flex w-[50%] flex-col text-end">
                        <div>Total Remaining Cash</div>
                        <div>إجمالي النقد المتبقي</div>
                      </div>
                      <div className="text-center">
                        {AppConfig.currency.icon()}{" "}
                        {formatMoney(firstAcceptedOrder.remainingCash)}
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : null}

            <div className="col-span-full mt-2 w-full border-b-2 border-b-black" />

            <div
              dir="ltr"
              className="ml-auto mt-2 flex w-[80%] items-center justify-between font-semibold"
            >
              <div className="flex w-[50%] flex-col text-end">
                <div>No. Products</div>
                <div>عدد المنتجات</div>
              </div>
              <div className="text-center">{invoiceData.totalItems}</div>
            </div>

            <div className="col-span-full mt-2 w-full border-b-2 border-b-black" />

            <div
              dir="ltr"
              className="ml-auto flex w-full items-center justify-between text-sm font-bold"
            >
              <div className="text-center">{invoiceData.paymentMethod}</div>
              <div className="flex flex-col text-end">
                <div>Payment method</div>
                <div>طريقة الدفع</div>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center text-center">
              <div className="h-52 w-52">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/d/d0/QR_code_for_mobile_English_Wikipedia.svg"
                  alt="QR"
                  className="h-full w-full object-contain"
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Cashier Modal (before Print) */}

      <div
        onClick={toggleCashierModal}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="z-[9999] flex max-h-[95vh] w-full max-w-[95%] flex-col gap-2 overflow-auto rounded-lg bg-white p-5 text-black md:w-1/2 lg:w-[600px]"
        >
          <div
            onClick={toggleCashierModal}
            className="self-end text-xl text-red-600"
          >
            <IoMdClose className="cursor-pointer" />
          </div>
          <div className="flex justify-between border px-2 py-5">
            <h2>{t("cashierHistory.clientNumber")}</h2>
            <span>
              {firstAcceptedOrder?.customer?.phone
                ? firstAcceptedOrder.customer.phone
                : t("cashierHistoryBody.guest")}
            </span>
          </div>
          <div className="flex justify-between border px-2 py-5">
            <h2>{t("cashierHistoryBody.GroupId")}</h2>
            <span>{invoiceData._id}</span>
          </div>

          <div className="flex items-center justify-between border px-2 py-5">
            <h2> {t("cashierHistory.orderType")}</h2>
            <span className="flex flex-col">
              {t("cashierHistoryBody.dineIn")}

              <span>
                {t("ordersBody.Floor")} :{" "}
                {firstAcceptedOrder?.dineIn?.floor?.number ||
                  t("cashierHistoryBody.notAssigned")}
              </span>

              <span>
                {t("ordersBody.Table")} :{" "}
                {firstAcceptedOrder?.dineIn?.table?.number ||
                  t("cashierHistoryBody.notAssigned")}
              </span>
            </span>
          </div>
          <div className="flex flex-col gap-3 border px-2 pt-2">
            <h2 className="font-bold">
              {t("cashierHistoryModal.orderDetails")}
            </h2>
            <div className="flex flex-col gap-1 text-[13px]">
              {invoiceData?.items.map((item: CartItem, index) => {
                return (
                  <div
                    key={index}
                    className={`flex flex-col gap-1 py-1 ${invoiceData?.items.length - 1 !== index && "border-b"}`}
                  >
                    <span className="font-bold" dir="ltr">
                      {t(item.nameEN, item.nameAR)} X {item.quantity}{" "}
                    </span>
                    {item.notes && (
                      <div className="flex flex-col">
                        <span className="text-[14px] font-semibold">
                          {t("cashierHistoryModal.itemNote")}
                        </span>
                        <span className="whitespace-pre-wrap break-words">
                          {item.notes && item.notes}
                        </span>
                      </div>
                    )}
                    {item.addons.length > 0 && (
                      <span className="flex flex-col">
                        {t("cashierHistoryModal.addons")}{" "}
                        <span className="flex flex-col font-normal">
                          {item.addons?.map((addon, i) => (
                            <span key={i}>{t(addon.nameEN, addon.nameAR)}</span>
                          ))}
                        </span>
                      </span>
                    )}
                    {item.options.length > 0 && (
                      <span className="flex flex-col">
                        {" "}
                        {t("cashierHistoryModal.addons")}
                        <span className="flex flex-col font-normal">
                          {item.options.map((option) =>
                            option.options.map((item, i) => (
                              <span key={i}>{t(item.nameEN, item.nameAR)}</span>
                            )),
                          )}
                        </span>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-3 border px-2 py-3">
            {invoiceData.couponDiscount
              ? invoiceData?.couponDiscount > 0 && (
                  <>
                    <div className="flex justify-between">
                      <h1>{t("cashierHistoryModal.usedCouponDiscount")}</h1>
                      <span
                        dir="rtl"
                        className="flex items-center gap-1 text-green-600"
                      >
                        {formatMoney(invoiceData?.couponDiscount)}{" "}
                        {AppConfig.currency.icon()}
                      </span>
                    </div>
                  </>
                )
              : null}

            <div className="flex justify-between">
              <h2>{t("cashierHistoryModal.total")}</h2>
              <div className="flex gap-2">
                <span dir={locale === "ar" ? "rtl" : "ltr"}>
                  {" "}
                  {formatMoney(invoiceData?.total)} {AppConfig.currency.icon()}
                </span>
                <span
                  className={`${isGroupPaid ? "text-green-600" : "text-red-500"}`}
                >
                  {isGroupPaid ? t("cashierHistoryBody.paid") : t("cashierHistoryBody.unpaid")}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end">
            <button
              onClick={() =>
                handlePrintForAllPlatforms(reactToPrintFn, contentRef)
              }
              className="my-btn w-fit self-center px-5 py-2"
            >
              {t("cashierHistoryModal.print")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
